import type { GroupedSelectPopoverProps } from '../GroupedSelectPopover';

import { BlockStack, Box, Divider, InlineStack, Button, Scrollable } from '@shopify/polaris';
import { SearchField } from '~/presentation/reports/ui/SearchFiled';
import { ActionListGroup } from '../../../ActionListGroup';
import { CheckboxGroup } from '../../../CheckboxGroup';
import { useTranslation } from 'react-i18next';
import { useItemSelector } from '../../hooks';
import { NoData } from '../NoData';
import { useEffect } from 'react';

interface IItemsSelectorProps {
  togglePopoverActive: (active: boolean) => void;
  control: GroupedSelectPopoverProps;
  active: boolean;
}

export const ItemsSelector: React.FC<IItemsSelectorProps> = (props) => {
  const { config, variant } = props.control;
  const { searchPlaceholder } = config || {};

  const { t } = useTranslation();

  const {
    hasItems,
    listItems,
    itemHovered,
    queryValue,
    height,
    maxHeight,
    tempSelectedItems,
    disableClearButton,
    disableApplyButton,
    dependentDisabledValues,
    handleHoverMetric,
    setTempSelectedItems,
    handleChangeSelection,
    setItemHovered,
    handleClearSelection,
    handleApplySelection,
    setQueryValue,
  } = useItemSelector(props);

  const getContent = () => {
    if (!hasItems) return <NoData boxProps={{ padding: '0' }} />;
    if (variant === 'choice') {
      return (
        <CheckboxGroup
          itemGroup={listItems}
          selected={tempSelectedItems}
          onHover={handleHoverMetric}
          itemHovered={itemHovered}
          onChange={setTempSelectedItems}
          hideTitle={!!queryValue}
          dependentDisabledValues={dependentDisabledValues}
        />
      );
    }
    return (
      <ActionListGroup
        itemGroup={listItems}
        selected={tempSelectedItems.length ? tempSelectedItems[0] : undefined}
        onHover={handleHoverMetric}
        onChange={handleChangeSelection}
        hideTitle={!!queryValue}
      />
    );
  };

  useEffect(() => {
    return () => {
      setItemHovered(undefined);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BlockStack>
      <div className="relative">
        <Box padding="300">
          <SearchField
            queryValue={queryValue}
            onSearch={setQueryValue}
            searchPlaceholder={searchPlaceholder}
          />
        </Box>
        <Scrollable
          scrollbarWidth="thin"
          style={{ height, maxHeight, overscrollBehavior: 'contain' }}
          className="p-3 pt-0"
        >
          {getContent()}
        </Scrollable>

        {variant === 'choice' && (
          <div className="sticky bottom-0 bg-white">
            <Divider borderColor="border" />
            <Box padding="300">
              <InlineStack gap="300" align="end">
                <Button
                  variant="plain"
                  onClick={handleClearSelection}
                  disabled={disableClearButton}
                >
                  {t('Clear')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleApplySelection}
                  disabled={disableApplyButton}
                >
                  {t('Apply')}
                </Button>
              </InlineStack>
            </Box>
          </div>
        )}
      </div>
    </BlockStack>
  );
};
